import { filledInputClasses } from '@mui/material/FilledInput'
import { inputBaseClasses } from '@mui/material/InputBase'
import { inputLabelClasses } from '@mui/material/InputLabel'
import { outlinedInputClasses } from '@mui/material/OutlinedInput'
import { CssVarsTheme, CssVarsThemeOptions, alpha } from '@mui/material/styles'

// ----------------------------------------------------------------------

export function textField(): CssVarsThemeOptions['components'] {
	const color = (theme: CssVarsTheme) => ({
		focused: theme.vars.palette.text.primary,
		active: theme.vars.palette.text.secondary,
		placeholder: theme.vars.palette.text.disabled,
	})

	const font = (theme: CssVarsTheme) => ({
		label: theme.typography.body1,
		value: theme.typography.body2,
	})

	return {
		// HELPER
		MuiFormHelperText: {
			styleOverrides: {
				root: ({ theme }) => ({
					marginTop: theme.spacing(1),
				}),
			},
		},

		// LABEL
		MuiFormLabel: {
			styleOverrides: {
				root: ({ theme }) => ({
					...font(theme).value,
					color: color(theme).placeholder,
					[`&.${inputLabelClasses.shrink}`]: {
						...font(theme).label,
						fontWeight: 600,
						color: color(theme).active,
						[`&.${inputLabelClasses.focused}`]: {
							color: color(theme).focused,
						},
						[`&.${inputLabelClasses.error}`]: {
							color: theme.palette.error.main,
						},
						[`&.${inputLabelClasses.disabled}`]: {
							color: theme.palette.text.disabled,
						},
						[`&.${inputLabelClasses.filled}`]: {
							transform: 'translate(12px, 6px) scale(0.75)',
						},
					},
				}),
			},
		},

		// BASE
		MuiInputBase: {
			styleOverrides: {
				root: ({ theme }) => ({
					[`&.${inputBaseClasses.disabled}`]: {
						'& svg': {
							color: theme.palette.text.disabled,
						},
					},
				}),
				input: ({ theme }) => ({
					...font(theme).value,
					'&::placeholder': {
						opacity: 1,
						color: color(theme).placeholder,
					},
				}),
			},
		},

		// STANDARD
		MuiInput: {
			styleOverrides: {
				underline: ({ theme }) => ({
					'&:before': {
						borderBottomColor: alpha(theme.palette.grey[500], 0.32),
					},
					'&:after': {
						borderBottomColor: color(theme).focused,
					},
				}),
			},
		},

		// OUTLINED
		MuiOutlinedInput: {
			styleOverrides: {
				root: ({ theme }) => ({
					[`&.${outlinedInputClasses.focused}`]: {
						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: color(theme).focused,
						},
					},
					[`&.${outlinedInputClasses.error}`]: {
						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: theme.palette.error.main,
						},
					},
					[`&.${outlinedInputClasses.disabled}`]: {
						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: theme.palette.action.disabledBackground,
						},
					},
				}),
				notchedOutline: ({ theme }) => ({
					borderColor: alpha(theme.palette.grey[500], 0.2),
					transition: theme.transitions.create(['border-color'], {
						duration: theme.transitions.duration.shortest,
					}),
				}),
			},
		},

		// FILLED
		MuiFilledInput: {
			styleOverrides: {
				root: ({ theme }) => ({
					borderRadius: theme.shape.borderRadius,
					backgroundColor: alpha(theme.palette.grey[500], 0.08),
					'&:hover': {
						backgroundColor: alpha(theme.palette.grey[500], 0.16),
					},
					[`&.${filledInputClasses.focused}`]: {
						backgroundColor: alpha(theme.palette.grey[500], 0.16),
					},
					[`&.${filledInputClasses.error}`]: {
						backgroundColor: alpha(theme.palette.error.main, 0.08),
						[`&.${filledInputClasses.focused}`]: {
							backgroundColor: alpha(theme.palette.error.main, 0.16),
						},
					},
					[`&.${filledInputClasses.disabled}`]: {
						backgroundColor: theme.palette.grey[300],
						[`& .${inputBaseClasses.input}`]: {
							'-webkit-text-fill-color': theme.palette.grey[700],
							color: theme.palette.grey[700], // Fallback
						},
					},
				}),
			},
		},
	}
}
